import { Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { InputNumberFieldV2 as InputNumberField } from "../../../../../../atoms/FormElement/InputNumberField";
import { DecimalUtils, formatCurrency, formatDateByType } from "../../../../../../libs/utils";
import { useBookingSelector } from "../../../../hooks/useBookingSelector";
import { useTorRules } from "../../../../hooks/useTorRules";
import type { BillingSchedule } from "../api";
import { GridHeaderCell } from "../GridHeaderCell";
import { gridTitleWithReset } from "../gridTitleWithReset";
import { BillingSchedulePreviewSection } from "./BillingSchedulePreviewSection";
import { BreakdownSequenceCell } from "./BreakdownSequenceCell";
import { BreakdownSummaryRow } from "./BreakdownSummaryRow";
import type { BreakdownFormValues } from "./types";

const { Text } = Typography;

/**
 * Derives a VAT multiplier from API row data.
 * Prefers `vatInPercentage` and rounds it to 4 decimal places to neutralise
 * server-side imprecision (e.g. the API can return 5.00003 instead of 5).
 * Falls back to vat/baseRent only when vatInPercentage is absent.
 */
function billingScheduleVatMultiplier(scheduleRow: BillingSchedule): number {
  const vatPercentFromApi = scheduleRow?.vatInPercentage;
  if (vatPercentFromApi !== undefined && vatPercentFromApi !== null && String(vatPercentFromApi).trim() !== "") {
    const vatPercentNumeric = Number(vatPercentFromApi);
    if (!Number.isNaN(vatPercentNumeric)) return parseFloat(vatPercentNumeric.toFixed(4)) * 0.01;
  }
  const baseRentNumeric = Number(scheduleRow?.baseRent);
  if (baseRentNumeric > 0) return (Number(scheduleRow?.vat) ?? 0) / baseRentNumeric;
  return 0;
}

type BillingScheduleBreakdownProps = {
  rows: BillingSchedule[];
  maxInstallments?: number;
  maxTorPeriod?: number;
  yearNumber?: number;
  hasValidTorType?: boolean;
  annualRent?: number;
  onRowsChange?: (rows: BillingSchedule[]) => void;
};

const cellStyle: React.CSSProperties = { marginBottom: 0 };

export const BillingScheduleBreakdown = (props: BillingScheduleBreakdownProps) => {
  const {
    rows,
    maxInstallments = 1,
    maxTorPeriod = 1,
    yearNumber = 1,
    hasValidTorType,
    annualRent,
    onRowsChange,
  } = props;

  const { isPaymentPlanApprovalSent } = useBookingSelector();
  const { isTurnoverRentOnly } = useTorRules();

  const lastSyncedRowsRef = useRef<string>("");

  const defaultValues = useMemo(
    () => ({
      rows: rows.map((scheduleRow: BillingSchedule) => {
        return {
          startDate: scheduleRow?.startDate,
          endDate: scheduleRow?.endDate,
          baseRent: scheduleRow?.baseRent,
          _vatRate: billingScheduleVatMultiplier(scheduleRow),
          assignedInstallmentNo:
            scheduleRow?.installment != null
              ? { label: scheduleRow.installment, value: scheduleRow.installment }
              : null,
          assignedTorPeriodNo:
            scheduleRow?.torPeriod != null ? { label: scheduleRow.torPeriod, value: scheduleRow.torPeriod } : null,
        };
      }),
    }),
    [rows],
  );

  const methods = useForm<BreakdownFormValues>({ defaultValues });

  const { reset, setValue, control } = methods;
  const watchedRows = useWatch({ control, name: "rows" });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!onRowsChange || !watchedRows) return;
    const scheduleRowsFromForm = rows.map((sourceScheduleRow, rowIndex) => ({
      baseRent: Number(watchedRows[rowIndex]?.baseRent ?? sourceScheduleRow?.baseRent),
      days: sourceScheduleRow?.days,
      endDate: sourceScheduleRow?.endDate,
      installment: watchedRows[rowIndex]?.assignedInstallmentNo?.value,
      noOfYear: sourceScheduleRow?.noOfYear,
      startDate: sourceScheduleRow?.startDate,
      ...(hasValidTorType ? { torPeriod: watchedRows[rowIndex]?.assignedTorPeriodNo?.value } : {}),
      total: sourceScheduleRow?.total,
      vat: sourceScheduleRow?.vat,
      vatInPercentage: sourceScheduleRow?.vatInPercentage,
    }));

    const serializedScheduleRows = JSON.stringify(scheduleRowsFromForm);
    if (lastSyncedRowsRef.current === serializedScheduleRows) return;
    lastSyncedRowsRef.current = serializedScheduleRows;

    onRowsChange(scheduleRowsFromForm);
  }, [watchedRows, hasValidTorType, rows, onRowsChange]);

  const tableData = rows.map((scheduleRow, rowIndex) => ({ ...scheduleRow, _idx: rowIndex }));

  const resetBaseRent = () =>
    rows.forEach((scheduleRow, rowIndex) => setValue(`rows.${rowIndex}.baseRent`, scheduleRow?.baseRent));

  const resetInstallments = () =>
    rows.forEach((_, rowIndex) => setValue(`rows.${rowIndex}.assignedInstallmentNo`, null));

  const resetTorPeriod = () => rows.forEach((_, rowIndex) => setValue(`rows.${rowIndex}.assignedTorPeriodNo`, null));

  const torPeriodColumn: ColumnsType<(typeof tableData)[number]>[number] = {
    title: gridTitleWithReset("TOR PERIOD #", resetTorPeriod, isPaymentPlanApprovalSent),
    key: "torPeriodNo",
    width: 140,
    render: (_, record) => (
      <BreakdownSequenceCell
        idx={record._idx}
        field="assignedTorPeriodNo"
        maxCount={maxTorPeriod}
        disabled={isPaymentPlanApprovalSent}
      />
    ),
  };

  const columns: ColumnsType<(typeof tableData)[number]> = [
    {
      title: "FROM",
      dataIndex: "startDate",
      key: "startDate",
      width: 120,
      render: (value) => formatDateByType(value),
    },
    {
      title: "TO",
      dataIndex: "endDate",
      key: "endDate",
      width: 120,
      render: (value) => formatDateByType(value),
    },
    {
      title: gridTitleWithReset("BASE RENT", resetBaseRent, isPaymentPlanApprovalSent),
      key: "baseRent",
      width: 160,
      render: (_, record) => (
        <InputNumberField
          fieldname={`rows.${record._idx}.baseRent`}
          control={control}
          format="currency"
          formItemProps={{ style: cellStyle }}
          inputNumberProps={{ placeholder: "0.00" }}
          disabled={isTurnoverRentOnly || isPaymentPlanApprovalSent}
        />
      ),
    },
    {
      title: "VAT",
      key: "vat",
      align: "right",
      width: 110,
      render: (_, record) => {
        const watchedFormRow = watchedRows?.[record._idx];
        const baseRentInput = watchedFormRow?.baseRent ?? 0;
        const vat = DecimalUtils.toNumber(DecimalUtils.multiply(baseRentInput, watchedFormRow?._vatRate ?? 0));
        return formatCurrency(vat, { symbol: false });
      },
    },
    {
      title: "TOTAL",
      key: "total",
      align: "right",
      width: 120,
      render: (_, record) => {
        const watchedFormRow = watchedRows?.[record._idx];
        const baseRentInput = watchedFormRow?.baseRent ?? 0;
        const vatInput = DecimalUtils.multiply(baseRentInput, watchedFormRow?._vatRate ?? 0);
        const total = DecimalUtils.toNumber(DecimalUtils.add([baseRentInput, vatInput]));
        return formatCurrency(total, { symbol: false });
      },
    },
    {
      title: gridTitleWithReset("INST #", resetInstallments, isPaymentPlanApprovalSent),
      key: "installmentNo",
      width: 120,
      render: (_, record) => (
        <BreakdownSequenceCell
          idx={record._idx}
          field="assignedInstallmentNo"
          maxCount={maxInstallments}
          disabled={isTurnoverRentOnly || isPaymentPlanApprovalSent}
        />
      ),
    },
    ...(hasValidTorType ? [torPeriodColumn] : []),
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <Text strong>Billing Schedule Breakdown</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Edit base rent values and assign installments
        </Text>
      </div>
      <FormProvider {...methods}>
        <Table
          dataSource={tableData}
          rowKey="_idx"
          columns={columns}
          pagination={false}
          size="small"
          summary={() => <BreakdownSummaryRow hasValidTorType={hasValidTorType} />}
          bordered
          components={{ header: { cell: GridHeaderCell } }}
        />
        <BillingSchedulePreviewSection
          rows={rows}
          hasValidTorType={hasValidTorType}
          annualRent={annualRent}
          yearNumber={yearNumber}
          maxInstallments={maxInstallments}
          maxTorPeriod={maxTorPeriod}
        />
      </FormProvider>
    </div>
  );
};
